import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemCard from '../../../components/item-content/SimpleItemCard';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import TableTab from '../../../components/tabs/table-tab';
import { Button } from '../../../components/button/Button';
import { toast } from "react-toastify";
import { DEF_PG_SIZE_SUB } from '../../../constants/maximum-page-size';
import { useLocale } from '../../../contexts/localeContext';
import { TableContextProvider } from '../../../contexts/table/TableContextProvider';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import zoneColumn from '../../../models/ColumnDefinitions/ZoneColumns';
import { CommonZone as CommonZoneModel } from '../../../models/CommonZone';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { ListQuery } from '../../../models/ListQuery';
import { Lookup } from '../../../models/Lookup';
import SiteSearch from '../../../models/search/SiteSearch';
import zoneSearch from '../../../models/search/ZoneSearch';
import { SimpleItemProfile } from '../../../models/SimpleItemProfile';
import { TabSummary } from '../../../models/TabSummary';
import { hideColumns } from "../../../models/utilities/columnHelper";
import { assignZone, getCommonZoneById, getCommonZoneNamesWithPagination, storeCommonZone } from '../../../services/commonZoneService';
import { getControllerNamesWithPagnation } from '../../../services/controllerService';
import { getSiteNamesWithPagination } from '../../../services/siteService';
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { getZoneNamesWithPagination, getZones } from '../../../services/zoneService';
import { IAppState } from '../../../store/types';
import { appendFilter } from '../../../utilities/filters';
import LookupTypes from "../../../models/LookupTypes";
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import AssignModalTwo from '@/components/assignWizard/AssignModal';
import { PaginationModel } from '@/models/Pagination';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const initialItem: CommonZoneModel = {
    name: '',
    id: 0,
    site: '',
    zones: 0,
    description: ''
}

const allItemsPath = '/access-control/hardware/common-zones';
const itemPath = '/access-control/hardware/common-zone/';

const CommonZone: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.zones), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<SimpleItemProfile>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>([])
    const [showManagedModal, setShowManagedModal] = useState(false);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const lookup: Lookup[] = [
        {
            name: LookupTypes.zoneBySiteId,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: 'controller',
            values: [],
            dataMethod: getControllerNamesWithPagnation,
            searchAccessor: 'controller.deviceImprox.devName'
        }
    ];

    const tabContentList: ContentWrapper[] = [{
        content: <TableContextProvider
            dataMethod={(listParameters?: ListQuery) => getZones({ ...listParameters, filters: appendFilter(itemId, 'Common Zone', 'commonZone.id', listParameters?.filters ?? []) })}
            tableDescriptor={hideColumns(zoneColumn, ['site', 'controller'])}
            searchDescriptor={zoneSearch}
            useTable
            usePagination
            useSearch
            useSort
            useFilter
            maxPageSize={DEF_PG_SIZE_SUB}
            lookup={lookup}>
            <TableTab
                title={translate.byKey(TranslationKeys.assignedZones)}
                selectRows={false}
                cyName="commonZone">
                <Button variant="submit" shape="rounded" id='manageButton' className="py-1 px-5"
                    onClick={() => setShowManagedModal(true)}>
                    {translate.byKey(TranslationKeys.manage)}
                </Button>
            </TableTab>
        </TableContextProvider>
    },
    {
        content: <Description title={translate.byKey(TranslationKeys.notes)} />
    }]

    const topic: Topic = {
        accessor: 'czName',
        title: translate.byKey(TranslationKeys.commonZones),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    }

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const reloadPage = async () => {
        try {
            setTabs([]);
            let commonZone;

            if (itemId) {
                commonZone = await getCommonZoneById(itemId);

                setSelectedItem({label: commonZone.name, value: commonZone.id.toString()})
                setCurrentItem(commonZone);
                const updatedTabs = updateTabBadgeTotal(0, commonZone?.zones, initialTabs);
                setTabs(updatedTabs);
            }
            else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[1]]);
            }
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.commonZones) },
                { value: itemPath + `${itemId}`, displayName: `${commonZone?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    useEffect(() => {
        setTabs([]);
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    //Basically get items to select when loading the assign wizard.
    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: any[] = []
            do {
                const zonesForCommonZone = await getZoneNamesWithPagination({ filters: itemId ? appendFilter(itemId, 'Common Zone', 'commonZone.id', []) : [], pageSize: 100, goToPage: pageNumber});
                ids = ids.concat(zonesForCommonZone.rows.map(zone => zone.value));
                rows = rows.concat(zonesForCommonZone.rows.map(row => ({id: row.value, name: row.label})));
                pagination = zonesForCommonZone.pagination;
                pageNumber++;
            } while(pageNumber <= pagination.totalPages);

            setSelectedIds(ids);
            setSelectedRows(rows);
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusyLoader(false);
        }
    }

    const getTabContent = (index?: number) => {
        const selectedTabContent = itemId ? tabContentList[index ?? 0] : tabContentList[1];
        return selectedTabContent.content;
    };

    const saveCommonZone = (values) => {
        const commonZone: CommonZoneModel = {
            ...currentItem,
            id: +itemId,
            name: values.name,
            siteId: itemId ? values.siteId : siteId,
            description: values.description
        }

        const isNew = itemId ? +itemId < 0 : true;

        saveItem(commonZone, isNew);
    }

    const assignZones = (toBeAdded: string[], toBeRemoved: string[]) => {
        setBusy(true);
        assignZone(itemId, toBeAdded, toBeRemoved)
            .then(async () => {
                toast.success(translate.byKey(TranslationKeys.success));
                reloadPage();
            }).catch((err) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(err) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    const saveItem = (item, createNewItem: boolean) => {
        setBusy(true);
        storeCommonZone(item, createNewItem)
            .then(async (value: any) => {
                setCurrentItem(item);
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.commonZoneSaved));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <div className='flex h-full'>
        <TopicContextProvider topic={topic} dataMethod={getCommonZoneNamesWithPagination} itemId={itemId} selectedItem={selectedItem}>
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveCommonZone}
                    tabs={tabs}
                    initialValues={currentItem}
                    itemPath={itemPath}>
                    <SimpleItemCard />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
        <AssignModalTwo
            isOpen={showManagedModal}
            dataMethod={(listParameters) => getZones({ ...listParameters, filters: appendFilter(currentItem?.siteId, 'Site', 'controller.deviceImprox.site.id', listParameters?.filters ?? []) })}
            lookup={lookup}
            onClose={() => { setShowManagedModal(false) }}
            assigningTitle={translate.byKey(TranslationKeys.zones)}
            modalTitle={translate.byKey(TranslationKeys.assignZones)}
            tableColumns={hideColumns(zoneColumn, ['site'])}
            searchColumns={zoneSearch}
            getAssignedItems={getAssignedItems}
            handleSubmit={assignZones} />
    </div>

}

export default CommonZone;
